///
/// EarthBrowser
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///
/// The data dock rail's item list: docked browser windows (explicit, in
/// tab order) followed by layers that are on the globe and have a table
/// but no window yet (derived, in catalog order). Turning a layer on puts
/// its icon on the rail without an "open browser" step; clicking the
/// derived icon runs the layer's browser command and the item becomes
/// explicit in place. See documentation/frontend/DATA_DOCK.md.
///

import { LAYERS, type LayerDefinition } from "@/components/controls/layers-panel/layerRegistry"
import type { DatasetBrowserWindowState } from "@/redux/dialogSlice"
import { useAppSelector } from "@/redux/store"
import { useMemo } from "react"
import { windowDatasetKey } from "./datasetWindowMeta"

export type DockItem =
	| { kind: "window"; id: string; window: DatasetBrowserWindowState }
	| { kind: "layer"; id: string; layer: LayerDefinition }

// Reference rows (city labels, borders, roads) are display preferences,
// not datasets to inspect; a default launch shouldn't grow a rail for
// them. Webcams share the group but are a browsable dataset
const cReferenceDockLayers = new Set(["webcam"])

/** Layers whose table should appear on the rail while the layer is on */
export function derivedDockLayers(
	visibleDatasets: string[],
	windows: DatasetBrowserWindowState[]
): LayerDefinition[] {
	const windowKeys = new Set(windows.map(w => windowDatasetKey(w.windowType)).filter(Boolean))
	return LAYERS.filter(layer => {
		if (!layer.commandId || !layer.visibility) return false
		if (layer.group === "Reference" && !cReferenceDockLayers.has(layer.id)) return false
		if (!visibleDatasets.includes(layer.visibility)) return false
		if (layer.parentId) {
			const parent = LAYERS.find(l => l.id === layer.parentId)
			if (parent?.visibility && !visibleDatasets.includes(parent.visibility)) return false
		}
		return !windowKeys.has(layer.visibility)
	})
}

export function useDockItems(): DockItem[] {
	const tabs = useAppSelector(state => state.dialog.bottomDrawer.tabs)
	const datasetWindows = useAppSelector(state => state.dialog.datasetWindows)
	const visibleDatasets = useAppSelector(state => state.earthbrowser.visibleDatasets)

	return useMemo(() => {
		const docked = tabs
			.map(tab => datasetWindows[tab.windowId])
			.filter((w): w is DatasetBrowserWindowState => w !== undefined)

		// Floating windows also claim their layer: a floated earthquake table
		// shouldn't get a second, derived icon on the rail
		const allWindows = Object.values(datasetWindows)

		const items: DockItem[] = docked.map(window => ({ kind: "window", id: window.id, window }))
		for (const layer of derivedDockLayers(visibleDatasets, allWindows)) {
			items.push({ kind: "layer", id: `layer:${layer.id}`, layer })
		}
		return items
	}, [tabs, datasetWindows, visibleDatasets])
}
